import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Bell, MessageCircle, Heart, UserPlus, Settings, Loader2, Eye, CheckCheck } from 'lucide-react';
import { NotificationItem } from '@/components/notifications/NotificationItem';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { notificationService } from '@/lib/api/notification-service';
import { useNotifications } from '@/contexts/NotificationContext';
import { NotificationType } from '@/types/notification';
import type { NotificationTypeValue, Notification } from '@/types/notification';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { VStack, HStack } from '@/components/core';

const ITEMS_PER_PAGE = 20;

type TabType = 'all' | 'messages' | 'matches' | 'interactions' | 'system';

const tabTypeToNotificationType: Record<Exclude<TabType, 'all'>, NotificationTypeValue[]> = {
  messages: [NotificationType.MESSAGE, NotificationType.FOLLOW_UP],
  matches: [NotificationType.MATCH],
  interactions: [NotificationType.LIKE, NotificationType.PROFILE_VISIT],
  system: [NotificationType.SYSTEM, NotificationType.COIN_PURCHASE, NotificationType.SUBSCRIPTION],
};

export function NotificationCenter() {
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { markAllAsRead, unreadCount } = useNotifications();
  const { user } = useAuth();
  const { t } = useTranslation(['notifications', 'common']);
  const isMobile = useMediaQuery('(max-width: 768px)');

  // Pagination state - accumulate notifications like Wallet
  const [allNotifications, setAllNotifications] = useState<Notification[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [page, setPage] = useState(0);

  // Ref for IntersectionObserver
  const observerRef = useRef<IntersectionObserver>();

  // Add page-notifications class to main for non-scrollable layout like wallet
  useEffect(() => {
    const main = document.querySelector('main.app-main');
    if (!main) return;
    main.classList.add('page-notifications');
    return () => {
      main.classList.remove('page-notifications');
    };
  }, []);

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'root_admin' || user?.role === 'demo_admin';

  // Get notification types for current tab
  const getNotificationTypes = useCallback((): NotificationTypeValue[] | undefined => {
    if (activeTab === 'all') return undefined;
    return tabTypeToNotificationType[activeTab];
  }, [activeTab]);

  // Query for initial notifications
  const { data: initialData, isLoading } = useQuery({
    queryKey: ['notifications', 'initial', activeTab],
    queryFn: () => {
      const types = getNotificationTypes();
      const params = {
        limit: ITEMS_PER_PAGE,
        offset: 0,
        ...(types && types.length === 1 && { notification_type: types[0] }),
      };
      return notificationService.getNotifications(params);
    },
  });

  // Initialize notifications when data loads
  useEffect(() => {
    if (initialData?.notifications) {
      setAllNotifications(initialData.notifications);
      setHasMore(initialData.notifications.length >= ITEMS_PER_PAGE);
      setPage(0);
    }
  }, [initialData]);

  // Load more function for infinite scroll
  const loadMore = useCallback(async () => {
    if (isFetchingMore || !hasMore) return;

    setIsFetchingMore(true);
    try {
      const nextPage = page + 1;
      const skip = nextPage * ITEMS_PER_PAGE;
      const types = getNotificationTypes();
      const params = {
        limit: ITEMS_PER_PAGE,
        offset: skip,
        ...(types && types.length === 1 && { notification_type: types[0] }),
      };

      const result = await notificationService.getNotifications(params);

      if (result.notifications && result.notifications.length > 0) {
        setAllNotifications(prev => [...prev, ...result.notifications]);
        setPage(nextPage);
        setHasMore(result.notifications.length >= ITEMS_PER_PAGE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more notifications:', error);
      setHasMore(false);
    } finally {
      setIsFetchingMore(false);
    }
  }, [page, isFetchingMore, hasMore, getNotificationTypes]);

  // Callback ref for infinite scroll trigger element
  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isLoading) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(entries => {
        const entry = entries[0];
        if (entry && entry.isIntersecting && hasMore && !isFetchingMore) {
          void loadMore();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [isLoading, hasMore, isFetchingMore, loadMore]
  );

  // Filter notifications
  const filteredNotifications = useMemo(() => {
    let notifications = allNotifications;

    // Filter out admin notifications for non-admin users
    if (!isAdmin) {
      const adminTypes = [
        NotificationType.SYSTEM_ALERT,
        NotificationType.ADMIN_ACTIVITY,
        NotificationType.PERFORMANCE_WARNING,
        NotificationType.USER_REPORT,
        NotificationType.PAYMENT_ISSUE,
        NotificationType.CONTENT_FLAGGED
      ];
      notifications = notifications.filter(n => !adminTypes.includes(n.type));
    }

    const types = getNotificationTypes();
    if (!types || types.length === 1) {
      return notifications;
    }

    // Filter for tabs with multiple notification types
    return notifications.filter(n => types.includes(n.type));
  }, [allNotifications, isAdmin, getNotificationTypes]);

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: markAllAsRead,
    onSuccess: () => {
      void queryClient.invalidateQueries(['notifications']);
    },
  });

  // Memoized tab change handler
  const handleTabChange = useCallback((value: string) => {
    setActiveTab(value as TabType);
  }, []);

  // Sync active tab from URL query (?tab=...)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    const allowedTabs: TabType[] = ['all', 'messages', 'matches', 'interactions', 'system'];
    const isValid = tabParam && allowedTabs.includes(tabParam as TabType);
    if (isValid) {
      const requested = tabParam as TabType;
      // Only allow 'system' tab for admins
      if (requested === 'system' && !isAdmin) {
        if (activeTab !== 'all') setActiveTab('all');
      } else if (activeTab !== requested) {
        setActiveTab(requested);
      }
    }
  }, [searchParams, isAdmin]);

  // Update URL when tab changes
  useEffect(() => {
    const current = searchParams.get('tab');
    if (current !== activeTab) {
      setSearchParams({ tab: activeTab });
    }
  }, [activeTab]);

  const getTabIcon = (tab: TabType) => {
    switch (tab) {
      case 'all':
        return <Bell size={16} />;
      case 'messages':
        return <MessageCircle size={16} />;
      case 'matches':
        return <UserPlus size={16} />;
      case 'interactions':
        return <Eye size={16} />;
      case 'system':
        return <Settings size={16} />;
    }
  };

  const getTabLabel = (tab: TabType) => {
    switch (tab) {
      case 'all':
        return t('common:navigation.all', 'Alle');
      case 'messages':
        return t('common:navigation.messages', 'Nachrichten');
      case 'matches':
        return t('common:navigation.matches', 'Matches');
      case 'interactions':
        return t('common:navigation.interactions', 'Aktivität');
      case 'system':
        return t('common:navigation.system', 'System');
    }
  };

  // Tabs available based on user role
  const availableTabs: TabType[] = isAdmin
    ? ['all', 'messages', 'matches', 'interactions', 'system']
    : ['all', 'messages', 'matches', 'interactions'];

  // Notification Item Skeleton Component
  const NotificationSkeleton = () => (
    <div className="space-y-token-sm">
      {Array.from({ length: 6 }).map((_, index) => (
        <HStack key={index} align="start" gap="sm" className="design-card p-token-md">
          <Skeleton className="w-10 h-10 rounded-full bg-white/10 shrink-0" />
          <VStack className="flex-1 min-w-0">
            <HStack align="center" justify="between">
              <Skeleton className="h-4 w-32 bg-white/10" />
              <Skeleton className="h-3 w-12 bg-white/10 shrink-0 ml-token-sm" />
            </HStack>
            <Skeleton className="h-4 w-full bg-white/10 mt-token-sm" />
            <Skeleton className="h-4 w-3/4 bg-white/10 mt-token-xs" />
          </VStack>
          <Skeleton className="w-8 h-8 rounded-token-sm bg-white/10 shrink-0" />
        </HStack>
      ))}
    </div>
  );

  // Empty State Component
  const EmptyState = () => (
    <HStack align="center" justify="center" className="flex-1">
      <div className={`design-card ${isMobile ? 'p-token-xl' : 'p-token-2xl'} text-center max-w-md fade-in-up`}>
        <HStack align="center" justify="center" className="w-20 h-20 gradient-primary-smooth rounded-full mx-auto mb-token-md">
          <Bell size={40} className="text-white" />
        </HStack>
        <h3 className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold design-text-primary mb-token-sm`}>
          {t('common:notifications.empty')}
        </h3>
        <p className="design-text-secondary mb-token-lg leading-relaxed">
          {t('notifications:center.emptyDescription', 'Hier erscheinen deine Benachrichtigungen')}
        </p>
      </div>
    </HStack>
  );

  // Main Layout - like Wallet with fixed header and scrollable content
  return (
    <VStack className="notification-layout w-full h-full min-h-0 p-token-md md:p-token-lg">
      {/* Background Effects - only on desktop */}
      {!isMobile && (
        <>
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-white/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-white/15 rounded-full blur-3xl pointer-events-none" />
        </>
      )}

      {/* Content - scrollable on mobile, fixed on desktop */}
      <VStack gap="md" className="relative lg:gap-token-lg flex-1 min-h-0 max-w-screen-lg mx-auto w-full">

        {/* Header Section - flex-shrink-0 to prevent shrinking */}
        <div className="flex-shrink-0">
          <VStack gap="md">
            {/* Page Header */}
            <div className={isMobile ? 'py-token-sm' : 'py-token-sm'}>
              <HStack align="center" justify="between">
                <HStack align="center" gap="md">
                  <HStack align="center" justify="center" className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} gradient-primary-smooth rounded-full`}>
                    <Bell size={isMobile ? 20 : 24} className="text-white" />
                  </HStack>
                  <VStack gap="none">
                    <h1 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold design-text-primary`}>
                      {t('center.title')}
                    </h1>
                    <p className="design-text-secondary text-sm">
                      {unreadCount > 0 ? t('center.unreadCount', { count: unreadCount }) : t('notifications:center.allRead', 'Alle gelesen')}
                    </p>
                  </VStack>
                </HStack>
                <HStack align="center" gap="sm">
                  {unreadCount > 0 && (
                    <>
                      <Badge className="bg-[var(--color-error)] text-white px-token-sm py-token-xs hidden sm:flex">
                        {unreadCount} {t('notifications:center.unread', 'ungelesen')}
                      </Badge>
                      <Button
                        variant="glass"
                        size="sm"
                        onClick={() => { markAllAsReadMutation.mutate(); }}
                        disabled={markAllAsReadMutation.isLoading}
                        className="hidden md:flex"
                      >
                        {markAllAsReadMutation.isLoading ? (
                          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCheck className="mr-1.5 h-4 w-4" />
                        )}
                        {t('center.markAllRead')}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate('/notifications/settings')}
                    className="design-text-secondary hover:design-text-primary"
                  >
                    <Settings className="h-5 w-5" />
                  </Button>
                </HStack>
              </HStack>
            </div>

            {/* Filter Tabs */}
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList className={isMobile ? 'w-full grid grid-cols-4' : undefined}>
                {availableTabs.map((tab) => (
                  <TabsTrigger
                    key={tab}
                    value={tab}
                    className={isMobile ? 'px-token-xs py-token-sm' : undefined}
                  >
                    <HStack align="center" gap={isMobile ? 'xs' : 'sm'}>
                      {getTabIcon(tab)}
                      {getTabLabel(tab)}
                    </HStack>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {/* Mobile mark all read */}
            {unreadCount > 0 && (
              <HStack justify="end" className="md:hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { markAllAsReadMutation.mutate(); }}
                  disabled={markAllAsReadMutation.isLoading}
                  className="text-xs design-text-secondary"
                >
                  {markAllAsReadMutation.isLoading && (
                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  )}
                  <CheckCheck className="mr-1.5 h-3 w-3" />
                  {t('center.markAllRead')}
                </Button>
              </HStack>
            )}
          </VStack>
        </div>

        {/* Notifications Container - takes remaining space with internal scroll */}
        <VStack className="flex-1 min-h-0">
          <VStack className="design-card h-full min-h-0 overflow-hidden">
            {isLoading ? (
              <div className="p-token-md">
                <NotificationSkeleton />
              </div>
            ) : filteredNotifications.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="flex-1 min-h-0 overflow-y-auto p-token-md">
                <div className="space-y-token-sm">
                  {filteredNotifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                    />
                  ))}
                </div>

                {/* Infinite scroll trigger */}
                {hasMore && (
                  <HStack
                    ref={loadMoreRef}
                    align="center"
                    justify="center"
                    className="h-16 mt-token-md"
                  >
                    {isFetchingMore && (
                      <VStack gap="sm" align="center">
                        <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
                        <p className="text-sm design-text-secondary">{t('center.loading')}</p>
                      </VStack>
                    )}
                  </HStack>
                )}
              </div>
            )}
          </VStack>
        </VStack>
      </VStack>
    </VStack>
  );
}

export default NotificationCenter;
